import { watchInviewNodes } from '@utilities/dom';
import { Warn, Trace } from '@utilities/logging';

export class PluginLoader {
  /**
   * PluginLoader constructor
   */
  constructor() {
    watchInviewNodes('.d3-plugin', this.launchPlugin.bind(this), this.detach.bind(this), {
      includeHidden: true,
    });
  }

  parse(el: HTMLElement, sel = '.d3-plugin') {
    const elements = el.matches(sel) ? [el] : el.querySelectorAll<HTMLElement>(sel);
    elements.forEach((e: HTMLElement) => {
      this.launchPlugin(e);
    });
  }

  detach(el: HTMLElement, sel = '.d3-plugin') {
    const elements = el.matches(sel) ? [el] : el.querySelectorAll<HTMLElement>(sel);
    elements.forEach((e: HTMLElement & { plugin?: any }) => {
      const pluginName = e.dataset.plugin,
        pluginInstance = e.plugin;

      if (!pluginInstance) return;

      if (typeof pluginInstance.off === 'function') pluginInstance.off();
      else if (typeof pluginInstance.detach === 'function') pluginInstance.detach();
      else Trace(`${pluginName} does not have off/detach method`);

      delete e.plugin;
    });
  }

  async getPlugin(pluginName: string) {
    try {
      return (await import(`../../plugins/${pluginName}`)).default;
    } catch (e) {
      Warn(`Plugin ${pluginName} not found`, e);
      return null;
    }
  }

  /**
   * launch the plugin
   */
  launchPlugin(el: HTMLElement & { plugin?: any }) {
    // plugin already set
    if (!el || el.plugin) return;

    const pluginName = el.dataset?.plugin;
    const names = pluginName?.split('|');
    if (!pluginName || !names?.length) {
      Warn('can not find data-plugin for element', el);
      return;
    }

    let options: any;
    try {
      options = JSON.parse(el.dataset.options ?? '{}');
    } catch (e) {
      Warn('Options parsing error for element', el, e, el.dataset.options);
      options = {};
    }

    names.forEach(async (pluginName) => {
      const PluginClass = await this.getPlugin(pluginName);

      if (!PluginClass) {
        Warn(`Plugin ${pluginName} does not exist`);
        return;
      }

      this.initPlugin(PluginClass, options, el, pluginName);
    });
  }

  async initPlugin(PluginClass: any, options: any, el: HTMLElement & { plugin?: any }, pluginName: string) {
    try {
      const pluginInstance = new PluginClass(el, options);
      el.plugin = pluginInstance;
      await pluginInstance.init();
    } catch (er) {
      Warn('plugin function error', er, el, options, pluginName);
    }
  }

  static readonly instance = new PluginLoader();
}

const pluginLoaderInstance = PluginLoader.instance;
export default pluginLoaderInstance;
